import { useEffect, useRef } from "react";

const useGlobalClickSound = (soundFile, enabled, volume = 0.1) => {
  const audioRef = useRef(null);

  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);

  // Load audio
  useEffect(() => {
    // Fallback audio (Desktop / Android)
    audioRef.current = new Audio(soundFile);
    audioRef.current.preload = "auto";

    const AudioCtx = window.AudioContext || window.webkitAudioContext;

    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    audioContextRef.current = ctx;

    const gainNode = ctx.createGain();
    gainNode.gain.value = volume;
    gainNode.connect(ctx.destination);

    gainNodeRef.current = gainNode;

    fetch(soundFile)
      .then((res) => res.arrayBuffer())
      .then((data) => ctx.decodeAudioData(data))
      .then((buffer) => {
        bufferRef.current = buffer;
      })
      .catch((err) => {
        console.error("Audio load error:", err);
      });

    return () => {
      try {
        ctx.close();
      } catch {}
    };
  }, [soundFile]);

  useEffect(() => {
    const playSound = async (event) => {
      if (!enabled) return;

      const target = event.target;

      const shouldPlay =
        target?.closest?.("button") ||
        target?.closest?.("a") ||
        target?.closest?.(".bg_btn") ||
        target?.closest?.(".butBtn") ||
        target?.closest?.(".menu-link") ||
        target?.closest?.(".iconShap") ||
        target?.closest?.(".pointercursor") ||
        target?.closest?.(".timeview") ||
        target?.closest?.("[data-bs-toggle]") ||
        target?.closest?.("[data-bs-target]") ||
        target?.closest?.("[data-bs-dismiss]") ||
        target?.closest?.("[onclick]") ||
        ["BUTTON", "SVG", "IMG", "SPAN", "LI", "I"].includes(target?.tagName);
      if (!shouldPlay) return;

      // Web Audio API (Best for iPhone)
      if (audioContextRef.current && bufferRef.current) {
        try {
          const ctx = audioContextRef.current;

          if (ctx.state === "suspended") {
            await ctx.resume();
          }

          // Stop previous sound to keep volume constant
          if (sourceRef.current) {
            try {
              sourceRef.current.stop();
            } catch {}
          }

          const source = ctx.createBufferSource();
          source.buffer = bufferRef.current;

          gainNodeRef.current.gain.value = volume;

          source.connect(gainNodeRef.current);
          source.start(0);

          sourceRef.current = source;
          return;
        } catch (err) {
          console.error(err);
        }
      }

      // Fallback Audio Element
      if (audioRef.current) {
        try {
          const audio = audioRef.current;

          audio.pause();
          audio.currentTime = 0;
          audio.volume = volume;

          await audio.play();
        } catch {}
      }
    };

    document.body.addEventListener("click", playSound, true);

    return () => {
      document.body.removeEventListener("click", playSound, true);
    };
  }, [enabled, volume]);

  return null;
};

export default useGlobalClickSound;
